import React, { useEffect, useState } from 'react';
import useHomeContext from '@/hooks/useHomeContext';
import { useLocation, useMatch, useNavigate } from 'react-router-dom';
import { routes } from '@/utils/routes';
import { HomeIcon, SquaresPlusIcon } from '@/components/Icons';
import RectangleStack from '@/components/Icons/RectangleStack';
import { ALL_ACCOUNTS } from '@/context/HomeProvider/HomeContextProvider';
import { useWallet } from '@/features/home/hooks/useWallet';
import { useFavoriteAccounts } from '@/features/home/hooks/useFavoriteAccounts';
import { colorVariant } from './colorVariant';
import cn from '@/utils/cn';
import { formatAmountOutput } from '@/utils/formatAmount';
import { sideNav } from '@/utils/translations';
import UserIcon from './UserIcon';
import SideNavMenuItem from './SideNavMenuItem';
import SideNavDivider from './SideNavDivider';
import SideNavSectionLabel from './SideNavSectionLabel';
import UserProfile from './UserProfile';
import AddFavoriteAccountsModal from './AddFavoriteAccountsModal';
import { useSideNavSections } from '@/versions/v3/features/home/hooks/useSideNavSections';
import { NavItemIdEnum, TResolvedNavItem } from '@/versions/v3/types/SideNav';
import { SIDE_NAV_SECTIONS } from '@/versions/v3/config/sideNavConfig';
import { useApprovalRequiredCount } from '@/features/payments/manage/hooks/useApprovalRequiredCount';
import ApprovalRequiredBadge from '@/features/payments/manage/components/ApprovalRequiredBadge';

interface SideNavBarProps {
  onOpenXeroModal: () => void;
}

const SIDEBAR_BASE = cn(
  colorVariant({ bg: 'navy-50', text: 'white' }),
  'flex flex-col h-screen overflow-hidden'
);

const HEADER = 'px-3 pt-4 pb-3';

const NAV_GROUP = 'flex flex-col gap-1.5';

const SCROLL_AREA = 'flex-1 overflow-y-auto vertical-scroll flex flex-col';

const SECTION = 'flex flex-col gap-2 px-3 py-4';

const NAV_SECTION = cn('flex flex-col gap-1.5 px-3 py-3', colorVariant({ border: 'navy-3' }));

const PROFILE_WRAPPER = cn('border-t pb-4', colorVariant({ border: 'navy-3' }));

const getActiveParentId = (pathname: string): string | null => {
  if (pathname.startsWith('/home/account/')) return NavItemIdEnum.ACCOUNTS;
  for (const section of SIDE_NAV_SECTIONS) {
    for (const item of section.items) {
      if (item.subItems?.some((sub) => sub.route === pathname)) return item.id;
    }
  }
  return null;
};

const SideNavBar = ({ onOpenXeroModal }: SideNavBarProps) => {
  const homeContext = useHomeContext()!;
  const { allWallets } = useWallet();
  const { favoriteAccountIds, removeFavoriteAccountId, setFavoriteAccountIds } =
    useFavoriteAccounts();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const accountDetailMatch = useMatch(routes.selectedAccount);
  const [expandedId, setExpandedId] = useState<string | null>(() => getActiveParentId(pathname));
  const [lastClickedSubItemId, setLastClickedSubItemId] = useState<string | null>(null);
  const [isAddFavoriteModalOpen, setIsAddFavoriteModalOpen] = useState(false);
  // homeContext.isLoading is a single flag shared with unrelated features (transaction history,
  // payments, etc.) - once wallets have loaded once, stop reading it so switching accounts
  // elsewhere in the app doesn't re-flash this submenu's skeleton.
  const [walletsLoadedOnce, setWalletsLoadedOnce] = useState(false);
  // No-ops (returns 0) when Feature.MANAGE_PAYMENT is off; this hook is still called
  // unconditionally here even if the Payments item is omitted from `sections` for other reasons.
  const approvalRequiredCount = useApprovalRequiredCount();

  useEffect(() => {
    if (!homeContext.isLoading) setWalletsLoadedOnce(true);
  }, [homeContext.isLoading]);

  const favoriteWallets = homeContext.wallets.filter((wallet) =>
    favoriteAccountIds.includes(wallet.id)
  );

  const handleToggle = (id: string) => setExpandedId((prev) => (prev === id ? null : id));

  useEffect(() => {
    const id = getActiveParentId(pathname);
    if (id) setExpandedId(id);
    setLastClickedSubItemId(null);
  }, [pathname]);

  useEffect(() => {
    const accountId = accountDetailMatch?.params?.walletId;
    if (!accountId || !allWallets.length) return;
    const wallet = allWallets.find((w) => w.id === accountId);
    if (!wallet || homeContext.selectedAccount.value === wallet.name) return;
    homeContext.setSelectedAccount({
      label: wallet.name,
      labelDescription: wallet.balance?.toString() || '0',
      value: wallet.name
    });
    homeContext.setBankAccount(wallet);
    homeContext.setAccountsFilter([wallet.id]);
  }, [accountDetailMatch?.params?.walletId, allWallets]);

  const isItemActive = (item: TResolvedNavItem): boolean =>
    !lastClickedSubItemId && item.route === pathname;

  const sections = useSideNavSections({ 'xero-modal': onOpenXeroModal });

  const handleSelectAccount = (optionLabel: string) => {
    if (optionLabel === ALL_ACCOUNTS) {
      homeContext.setBankAccount(homeContext.allAccounts);
      homeContext.setSelectedAccount(homeContext.defaultOption);
      homeContext.setAccountsFilter([]);
    } else {
      const found = allWallets.find((w) => w.name === optionLabel);
      if (found) {
        homeContext.setSelectedAccount({
          label: found.name,
          labelDescription: found.balance?.toString() || '0',
          value: found.name
        });
        homeContext.setBankAccount(found);
        homeContext.setAccountsFilter([found.id]);
      }
    }
  };

  return (
    <aside className={cn(SIDEBAR_BASE, 'w-52')}>
      {/* Fixed header: user icon only - no collapse toggle, per Figma (fixed-width sidebar) */}
      <div className={HEADER}>
        <UserIcon />
      </div>

      {/* Scrollable Content — Home/Accounts + config-driven sections */}
      <div className={SCROLL_AREA}>
        {/* Home & Accounts — bespoke: Accounts subItems are dynamic from useWallet */}
        <div className={NAV_SECTION}>
          <div className={NAV_GROUP}>
            <SideNavMenuItem
              icon={HomeIcon}
              label={sideNav.home}
              isActive={pathname === routes.home && !lastClickedSubItemId}
              onClick={() => {
                setLastClickedSubItemId(null);
                setExpandedId(null);
                homeContext.setSelectedAccount(homeContext.defaultOption);
                homeContext.setBankAccount(homeContext.allAccounts);
                homeContext.setAccountsFilter([]);
                navigate(routes.home);
              }}
            />
            <SideNavDivider />
            <SideNavMenuItem
              icon={RectangleStack}
              label={sideNav.accounts}
              isExpanded={expandedId === NavItemIdEnum.ACCOUNTS}
              onToggle={() => handleToggle(NavItemIdEnum.ACCOUNTS)}
              loading={!walletsLoadedOnce && expandedId === NavItemIdEnum.ACCOUNTS}
              hideChevron
              headerAction={
                <button
                  type="button"
                  aria-label={sideNav.addFavoriteAccounts}
                  title={sideNav.addFavoriteAccounts}
                  disabled={!walletsLoadedOnce}
                  onClick={() => setIsAddFavoriteModalOpen(true)}
                  className="flex-shrink-0 rounded-full p-1 opacity-50 hover:opacity-100 transition-opacity disabled:cursor-not-allowed disabled:opacity-30">
                  <SquaresPlusIcon size="xs" color="white" />
                </button>
              }
              subItems={favoriteWallets.map((wallet) => ({
                id: wallet.id,
                label: wallet.name,
                description: `$${formatAmountOutput(String(wallet.balance ?? 0))}`,
                isActive: homeContext.bankAccount?.id === wallet.id,
                onClick: () => {
                  // by name (handleSelectAccount) - a name lookup can land on a different wallet
                  // when two favorites share a display name, leaving bankAccount.id out of sync
                  // with the URL and triggering EmergeAccounts' loading-screen gate.
                  homeContext.setSelectedAccount({
                    label: wallet.name,
                    labelDescription: wallet.balance?.toString() || '0',
                    value: wallet.name
                  });
                  homeContext.setBankAccount(wallet);
                  homeContext.setAccountsFilter([wallet.id]);
                  navigate(routes.selectedAccount.replace(':walletId', wallet.id));
                },
                onRemove: () => {
                  removeFavoriteAccountId(wallet.id);
                  if (homeContext.bankAccount?.id === wallet.id) {
                    handleSelectAccount(ALL_ACCOUNTS);
                    navigate(routes.home);
                  }
                }
              }))}
            />
          </div>
        </div>

        {/* Config-driven sections — filtered by account type and feature flags */}
        {sections.map((section) => (
          <div key={section.id} className={SECTION}>
            <SideNavSectionLabel label={section.label} />
            {section.items.map((item) => (
              <SideNavMenuItem
                key={item.id}
                icon={item.icon}
                label={item.label}
                isExpanded={expandedId === item.id}
                onToggle={() => handleToggle(item.id)}
                isActive={isItemActive(item)}
                onClick={item.onClick}
                headerAction={
                  item.id === NavItemIdEnum.PAYMENTS ? (
                    <ApprovalRequiredBadge count={approvalRequiredCount} />
                  ) : undefined
                }
                subItems={item.subItems?.map((sub) => ({
                  ...sub,
                  // A non-route sub-item (e.g. "Xero connections", which only opens a modal)
                  // doesn't change the URL, so a previously-active route-based sibling (e.g.
                  // "General ledger settings") would otherwise stay lit up too - once anything
                  // has been explicitly clicked, that click alone decides which single sub-item
                  // is active; route matching only applies once lastClickedSubItemId is cleared
                  // again (a real navigation - see the pathname effect above).
                  isActive: lastClickedSubItemId
                    ? sub.id === lastClickedSubItemId
                    : sub.route === pathname,
                  onClick: () => {
                    setLastClickedSubItemId(sub.route ? null : sub.id ?? null);
                    sub.onClick?.();
                  }
                }))}
              />
            ))}
          </div>
        ))}
      </div>

      {/* User Profile - Bottom */}
      <div className={PROFILE_WRAPPER}>
        <UserProfile />
      </div>

      <AddFavoriteAccountsModal
        isOpen={isAddFavoriteModalOpen}
        onClose={() => setIsAddFavoriteModalOpen(false)}
        wallets={homeContext.wallets}
        favoriteAccountIds={favoriteAccountIds}
        onApply={(walletIds) => {
          setFavoriteAccountIds(walletIds);
          setExpandedId(NavItemIdEnum.ACCOUNTS);
        }}
      />
    </aside>
  );
};

export default SideNavBar;
